import React, { ReactNode } from 'react'
import { Paper, Grid, Typography } from '@material-ui/core'
import { useTranslation } from 'react-i18next'

import { Status, Tooltip } from '@percent/admin-dashboard/common/components'
import { Variant } from '@percent/admin-dashboard/common/components/status/Status.types'
import styles from './NonProfitDetailHeader.module.scss'
import { Alert, Badge, Button, Spacer } from '@percent/lemonade'
import { statusMap } from '@percent/admin-dashboard/constants/statusMap'

type NonProfitDetailHeaderProps = {
  nonprofitName: string
  status: Variant
  setIsRejectModalOpened: (isOpened: boolean) => void
  setIsApproveModalOpened: (isOpened: boolean) => void
  notValidForProgram?: boolean
  disableConfirm?: boolean
  isApproved?: boolean
  isRejected?: boolean
  disableReject?: boolean
  canAccept?: boolean
  canReject?: boolean
  canSubmitQc?: boolean
  setIsSubmitQcModalOpened?: (isOpened: boolean) => void
  tabs?: ReactNode
}

export function NonProfitDetailHeader({
  nonprofitName,
  status,
  setIsRejectModalOpened,
  setIsApproveModalOpened,
  notValidForProgram,
  disableConfirm,
  isApproved,
  isRejected,
  disableReject,
  canAccept = true,
  canReject = true,
  canSubmitQc,
  setIsSubmitQcModalOpened,
  tabs
}: NonProfitDetailHeaderProps) {
  const { t } = useTranslation()

  const isPending = !isApproved && !isRejected
  const showDecisionButtons = status === Status.variants.PENDING && (canAccept || canReject)

  return (
    <Grid container spacing={0} component={Paper} elevation={0} className={styles.container}>
      <Grid item xs={12} className={tabs ? styles.nonprofitDetailWithTabs : styles.nonprofitDetail}>
        {isPending && disableConfirm && (
          <Grid item xs={12} className={styles.alertContainer}>
            <Alert variant="info" title={t('typography.flagsNeedToBeResolved')} />
          </Grid>
        )}
        <div className={styles.ellipsisParent}>
          <div className={styles.ellipsisContainer}>
            <div className={styles.ellipsisText}>
              <Tooltip placement="top-start" title={nonprofitName}>
                <Typography variant="h6" className={styles.heading} data-testid="validation-request-organisation-name">
                  {nonprofitName}
                </Typography>
              </Tooltip>
              <div className={styles.badgesContainer}>
                <Badge variant={statusMap[status].variant} icon={statusMap[status].icon}>
                  <span>{t(statusMap[status].text)}</span>
                </Badge>
                {notValidForProgram ? (
                  <Badge variant="critical" icon="reject">
                    {t('typography.notValidForProgram')}
                  </Badge>
                ) : null}
              </div>
            </div>
            {showDecisionButtons || canSubmitQc ? (
              <div className={styles.buttonsContainer}>
                {showDecisionButtons ? (
                  <>
                    {canReject ? (
                      <Button
                        onClick={() => setIsRejectModalOpened(true)}
                        variant="reject"
                        data-testid="reject-btn"
                        disabled={disableReject}
                      >
                        {t('button.reject')}
                      </Button>
                    ) : null}
                    {canReject && canAccept ? <Spacer size={2} /> : null}
                    {canAccept ? (
                      <Button
                        onClick={() => setIsApproveModalOpened(true)}
                        data-testid="submit-btn"
                        disabled={disableConfirm}
                      >
                        {t('button.submit')}
                      </Button>
                    ) : null}
                  </>
                ) : null}
                {canSubmitQc ? (
                  <>
                    {showDecisionButtons ? <Spacer size={2} /> : null}
                    <Button onClick={() => setIsSubmitQcModalOpened?.(true)} data-testid="submit-qc-btn">
                      {t('dialog.submitQc.title')}
                    </Button>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        {tabs ? <div className={styles.tabs}>{tabs}</div> : null}
      </Grid>
    </Grid>
  )
}
